<script setup lang="ts">
import BaseModalWindow from '@/common/components/templates/BaseModalWindow.vue';
import DetailInformation from '@/common/components/molecules/DetailInformation.vue';
import StatusTag from '@/common/components/molecules/statusTag/StatusTag.vue';
import TagItem from '@/common/components/molecules/tag/TagItem.vue';
import { kebabCase } from 'lodash-es';
import ToggleBox from '@/common/components/molecules/box/ToggleBox.vue';
import TextViewer from '@/common/components/molecules/textViewer/TextViewer.vue';
import { type Props, type Emit, setup } from './notificationStatusWindow.setup';

const props = defineProps<Props>();
const emit = defineEmits<Emit>();

const {
  t,
  validateJson,

  isShow,
  selectedTab,
  tabPanels,
  notificationStatusInfo,
} = setup(props, emit);
</script>

<template>
  <base-modal-window
    v-model:visible="isShow"
    :title="t('WORD.NOTIFICATION_STATUS')"
    window-class="notification-status-window"
    size-type="CUSTOM"
    :size="{
      width: '1000px',
      height: '660px',
    }"
  >
    <ev-tabs
      v-model="selectedTab"
      :panels="tabPanels"
      stretch
    >
      <ev-tab-panel
        v-for="item in tabPanels"
        :key="item.value"
        :text="item.text"
        :value="item.value"
      >
        <section class="notification-status-information">
          <detail-information
            v-for="(infoItem, index) in notificationStatusInfo"
            :key="`${props.resultId}_${item.value}_${index}`"
            :title="infoItem.title"
            :use-dashboard-setting="false"
            :use-header="!!infoItem.title"
          >
            <template #content>
              <template
                v-for="(info, index2) in notificationStatusInfo[index].info"
                :key="`${props.resultId}_${index}_${index2}`"
              >
                <dl
                  v-if="info.renderType === 'row'"
                  :class="['notification-status-row', kebabCase(info.field)]"
                >
                  <dt class="notification-status-row__label">{{ info.label }}</dt>
                  <dd
                    v-if="info.field === 'status'"
                    class="notification-status-row__value"
                  >
                    <status-tag :type="info.value">{{ info.value }}</status-tag>
                  </dd>
                  <dd
                    v-else-if="info.field === 'receivers' || info.field === 'notifiedMethod'"
                    class="notification-status-row__value tag-list"
                  >
                    <tag-item
                      v-for="(name, nameIdx) in info.value"
                      :key="`${name}${nameIdx}`"
                      :title="name"
                      size="xsmall"
                      html-type="span"
                    >
                      {{ name }}
                    </tag-item>
                  </dd>
                  <dd
                    v-else
                    class="notification-status-row__value"
                  >
                    {{ info.value }}
                  </dd>
                </dl>
                <toggle-box
                  v-else-if="info.renderType === 'viewer'"
                  size="small"
                  is-expanded
                  class="notification-status-viewer"
                >
                  <template #title> {{ info.label }} </template>
                  <text-viewer
                    :text="info.value"
                    :type="validateJson(info.value) ? 'json' : 'none'"
                    use-line-numbers
                  />
                </toggle-box>
                <div
                  v-else-if="info.renderType === 'onSiteMessage'"
                  class="on-site-message"
                >
                  <p
                    v-if="info.value.title"
                    class="on-site-message__title"
                  >
                    {{ info.value.title }}
                  </p>
                  <p
                    v-if="info.value.info"
                    class="on-site-message__info"
                  >
                    {{ info.value.info }}
                  </p>
                  <p
                    v-if="info.value.content"
                    class="on-site-message__content"
                  >
                    {{ info.value.content }}
                  </p>
                </div>
              </template>
            </template>
          </detail-information>
        </section>
      </ev-tab-panel>
    </ev-tabs>
  </base-modal-window>
</template>

<style lang="scss">
.notification-status-window {
  .window-content {
    height: 100%;
  }
  .ev-window-content {
    padding: 0 !important;
  }
  .ev-tabs-body {
    height: 100%;
    min-height: 0;
    padding: 10px !important;
    overflow-y: auto;
  }
  .detail-information__content {
    padding: 0 16px;
  }
}
</style>
<style scoped lang="scss">
.notification-status-information {
  .detail-information:not(:last-child) {
    margin-bottom: 10px;
  }
}
.notification-status-viewer {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin: 12px 0;
}
.on-site-message {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 140px;
  padding: 12px 16px;
  margin: 12px 0;
  border: 1px solid var(--color-gray-05-05);
  border-radius: 2px;
  overflow-wrap: break-word;

  &__title {
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
  }

  &__info {
    font-size: 12px;
    font-weight: 400;
    line-height: 1.4;
    white-space: pre-wrap;
  }

  &__content {
    font-size: 13px;
    font-weight: 400;
    line-height: 1.4;
    white-space: pre-wrap;
  }
}
.notification-status-row {
  display: flex;
  align-items: start;
  margin: 12px 0;

  &:not(.status, .send-time, :last-child) {
    padding-bottom: 12px;
    border-bottom: 1px solid var(--color-gray-05-05);
  }

  &__label {
    display: flex;
    align-items: center;
    min-width: 100px !important;
    height: 18px;
    margin-right: 8px;
    color: var(--color-gray-02-10);
  }

  &__value {
    display: flex;
    flex: 1;
    min-width: 0;
    line-height: 18px;

    &.tag-list {
      flex-wrap: wrap;
      gap: 4px;
      width: calc(100% - 116px);
    }
    :deep(.tag-item) {
      width: fit-content !important;
      max-width: 100%;

      @include shortening();
    }
  }
}
:deep(.ev-tabs-title.has-icon) {
  display: flex;
  position: relative !important;
  gap: 6px;
  align-items: center;
  i {
    width: 16px;
    height: 16px;
    margin: 0 4px;
  }
}

// 스타일 가이드에 맞춰 수정, 공통으로 사용할 수 있도록 추후 수정 필요함
:deep(.text-viewer__text-area) {
  flex: none;
  min-height: auto;
  max-height: 500px;
  overflow: auto;
  resize: vertical;

  // .vjs-tree {
  //   padding-left: 40px !important;
  //   background-color: var(--color-gray-06-02);
  //   color: var(--main-font-color);
  // }
  // .vjs-tree-node {
  //   border: none;
  //   background-color: var(--color-gray-08-00);

  //   &.has-carets {
  //     @include typography('button-xsmall');
  //   }
  // }
  // .vjs-node-index {
  //   color: var(--color-gray-02-10);
  // }
  // .vjs-key {
  //   color: var(--color-yellow-03-07);
  //   .vjs-colon {
  //     color: var(--main-font-color);
  //   }
  // }
  // .vjs-value {
  //   color: var(--color-green-03-07);
  // }
}
</style>
